import React, {useEffect, useState, useCallback, useContext} from 'react'
import PropTypes from 'prop-types'
import {Trans} from '@lingui/macro'
import {createCalendarGrid} from '_helper/functions'
import Cell from '_component/Cell'
import DayDetailCell from '_component/dayDetail/DayDetailCell'
import WeekDetail from '_component/weekDetail/WeekDetail'
import {closeAllDetails, fillCalendarWithDates, getDate} from '_helper/calendarFunctions'
import RoundedButton from '_component/RoundedButton'
import {ReactComponent as Microphone} from '_asset/svg/microphone.svg'
import {ReactComponent as People} from '_asset/svg/people.svg'
import {useSearchParams} from 'react-router-dom'
import {Dialog} from '@mui/material'
import EventDetail from './eventDetail/EventDetail'
import {EventsContext} from '_helper/context'

const Calendar = (props) => {
  const {selectedMonth, selectedYear, id, date, eventType} = props
  const [updateCalendar, setUpdateCalendar] = useState(false)
  // TODO add loading ID and date from url (auto open event detail)
  const [searchParams, setSearchParams] = useSearchParams()
  const [grid, setGrid] = useState(createCalendarGrid())
  const [detail, setDetail] = useState({rowIndex: null, colIndex: null, type: 'week'})
  const [platformFilter, setPlatformFilter] = useState(null)
  const [privacyFilter, setPrivacyFilter] = useState(null)
  const [eventDetailVisibility, setEventDetailVisibility] = useState(false)
  let {dispatch} = useContext(EventsContext)

  useEffect(() => {
    if (id && date) {
      dispatch({type: 'SET_EVENT_DATA', data: {eventId: id, date: date}})
      setEventDetailVisibility(true)
    }
  }, [])

  useEffect(() => {
    let newGrid = closeAllDetails([...grid])
    setGrid(newGrid)
  }, [])

  useEffect(() => {
    fillCalendarWithDates(selectedYear, selectedMonth, grid, setGrid)

    // update calendar on every refill with new dates
    setUpdateCalendar((prevState) => !prevState)

    let newGrid = closeAllDetails([...grid])

    setDetail({rowIndex: null, colIndex: null, type: 'week'})
    newGrid.forEach((row, rowIndex) => {
      //add week detail under every non detail row
      newGrid.splice(rowIndex * 2 + 1, 0, 'week-detail')
    })

    setGrid(newGrid)
  }, [selectedMonth])

  const onDayClick = (row) => (col) => (id) => (event) => {
    handleOpenEventDetail(id, grid[row - 1][col])
  }

  const handleOpenEventDetail = useCallback(
    (eventId, date) => {
      const newDate = getDate(date.date)
      setSearchParams((prevState) => ({
        ...prevState,
        date: newDate,
        id: eventId,
        ...(searchParams.get('event_type') && {event_type: searchParams.get('event_type')}),
      }))
      setEventDetailVisibility(true)
      dispatch({type: 'SET_EVENT_DATA', data: {eventId, date: newDate}})
    },
    [dispatch]
  )

  const handleCalendarFilter = (filter) => (event) => {
    if (platformFilter === filter) {
      return setPlatformFilter(null)
    }
    setPlatformFilter(filter)
  }

  const handlePrivacyFilter = (filter) => (event) => {
    if (privacyFilter === filter) {
      return setPrivacyFilter(null)
    }
    setPrivacyFilter(filter)
  }

  const handleCloseDialog = () => {
    setEventDetailVisibility(false)
    dispatch({type: 'SET_EVENT_DATA', data: {}})
  }

  // Filtering

  return (
    <div className="table-wrapper">
      <table className="calendar-table" id="main-table">
        <thead>
          <tr>
            <th colSpan={7}>
              <div className="control-buttons">
                <div className="filters">
                  <RoundedButton
                    selected={platformFilter === 'ONLINE'}
                    onClick={handleCalendarFilter('ONLINE')}
                    text={<Trans>Online events</Trans>}
                    icon={<Microphone />}
                  />
                  <RoundedButton
                    selected={platformFilter === 'OFFLINE'}
                    onClick={handleCalendarFilter('OFFLINE')}
                    text={<Trans>Local events</Trans>}
                    icon={<People />}
                  />
                  <RoundedButton
                    selected={privacyFilter === 'REGISTRATION'}
                    onClick={handlePrivacyFilter('REGISTRATION')}
                    text={<Trans>With Registration</Trans>}
                    icon={<People />}
                  />
                  <RoundedButton
                    selected={privacyFilter === 'PUBLIC'}
                    onClick={handlePrivacyFilter('PUBLIC')}
                    text={<Trans>Public</Trans>}
                    icon={<People />}
                  />
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {grid.map((row, rowIndex) =>
            row === 'week-detail' ? (
              <WeekDetail
                key={rowIndex}
                dates={grid[rowIndex - 1]}
                onDayClick={onDayClick(rowIndex, row)}
                updateCalendar={updateCalendar}
                platformFilter={platformFilter}
                privacyFilter={privacyFilter}
                eventType={eventType}
              />
            ) : (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) =>
                  cell === 'day-detail' ? (
                    <DayDetailCell // phone view of detail cell
                      isEventDetailVisible={eventDetail.visible}
                      eventId={Number(eventDetail.id)}
                      key={`${rowIndex}-${colIndex}`}
                      type="cell"
                      data={grid[detail.rowIndex][detail.colIndex]}
                    />
                  ) : (
                    <Cell cell={cell} key={`${rowIndex}-${colIndex}`} />
                  )
                )}
              </tr>
            )
          )}
        </tbody>
      </table>
      <Dialog
        sx={{
          '& .MuiPaper-root': {
            maxWidth: '100%',
            backgroundColor: 'initial',
            width: '100%',
          },
        }}
        onClose={handleCloseDialog}
        open={eventDetailVisibility}
      >
        <EventDetail closeEventDetail={handleCloseDialog} />
      </Dialog>
    </div>
  )
}

Calendar.propTypes = {
  selectedMonth: PropTypes.number.isRequired,
  selectedYear: PropTypes.number.isRequired,
  mobileView: PropTypes.bool,
}

export default Calendar
